/**
 * fetch-Wrapper — Pendant zur HttpClient-Konfiguration aus `Program.cs` des Altclients
 * inklusive `CustomAuthorizationMessageHandler` und `GraphAuthorizationMessageHandler`.
 */

import { API_BASE_URL, API_SOURCE_HEADER, GRAPH_BASE_URL } from '@/config/appConfig';
import { API_SCOPES, GRAPH_SCOPES } from '@/config/authConfig';

import { toCanonicalJson, toWireJson } from './jsonNaming';
import { acquireToken } from './requestContext';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

/** Antwort mit Fehlerstatus; `body` ist der ungeparste Antworttext. */
export class ApiError extends Error {
  readonly status: number;
  readonly body: string;

  constructor(status: number, body: string, message?: string) {
    super(message ?? `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/** Pendant zu `ChinaAppUserFriendlyException`: Überschrift plus Detailtext. */
export class UserFriendlyError extends Error {
  readonly caption: string;

  constructor(caption: string, message: string) {
    super(message);
    this.name = 'UserFriendlyError';
    this.caption = caption;
  }
}

export interface HttpClient {
  /** Wirft `ApiError` bei `!response.ok`, liefert `null` bei 204/leerem Body. */
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  put<T>(path: string, body?: unknown): Promise<T>;
  delete<T>(path: string): Promise<T>;
  /** Liefert die rohe Response, wirft nie wegen Statuscode. */
  raw(method: HttpMethod, path: string, body?: unknown): Promise<Response>;
}

interface HttpClientOptions {
  baseUrl: string;
  scopes: string[];
  withSourceHeader: boolean;
  /**
   * Wandelt Schlüssel zwischen camelCase (Leitung) und PascalCase (Modell) um. Gilt nur für die
   * API: Microsoft Graph ist von sich aus camelCase und wird in `graphApi.ts` so gelesen.
   */
  normalizeJson: boolean;
}

async function parseJson<T>(response: Response, normalizeJson: boolean): Promise<T> {
  const text = await response.text();
  if (text.length === 0) {
    return null as T;
  }
  const parsed: unknown = JSON.parse(text);
  return normalizeJson ? toCanonicalJson<T>(parsed) : (parsed as T);
}

/**
 * Liest den Antwortkörper einer API-Antwort als JSON. Ein leerer Körper (auch HTTP 204) ergibt
 * `null` — `ReadFromJsonAsync` des Altclients verhält sich genauso.
 *
 * Die API serialisiert camelCase; die Schlüssel werden auf die kanonischen PascalCase-Namen des
 * Modells zurückgeführt (siehe `jsonNaming.ts`).
 */
export async function readJson<T>(response: Response): Promise<T> {
  return parseJson<T>(response, true);
}

function buildUrl(baseUrl: string, path: string): string {
  return `${baseUrl}/${path.replace(/^\/+/, '')}`;
}

function createHttpClient({
  baseUrl,
  scopes,
  withSourceHeader,
  normalizeJson,
}: HttpClientOptions): HttpClient {
  async function buildHeaders(hasBody: boolean): Promise<Headers> {
    const headers = new Headers();
    if (withSourceHeader) {
      headers.set(API_SOURCE_HEADER.name, API_SOURCE_HEADER.value);
    }
    if (hasBody) {
      headers.set('Content-Type', 'application/json');
    }
    // Ohne Token wird ohne Authorization gesendet — wie der AuthorizationMessageHandler,
    // solange kein Zugriffstoken vorliegt.
    const token = await acquireToken(scopes);
    if (token !== null) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }

  async function raw(method: HttpMethod, path: string, body?: unknown): Promise<Response> {
    const hasBody = body !== undefined;
    return fetch(buildUrl(baseUrl, path), {
      method,
      headers: await buildHeaders(hasBody),
      body: hasBody ? JSON.stringify(normalizeJson ? toWireJson(body) : body) : undefined,
    });
  }

  async function send<T>(method: HttpMethod, path: string, body?: unknown): Promise<T> {
    const response = await raw(method, path, body);
    if (!response.ok) {
      throw new ApiError(response.status, await response.text());
    }
    return parseJson<T>(response, normalizeJson);
  }

  return {
    get: (path) => send('GET', path),
    post: (path, body) => send('POST', path, body),
    put: (path, body) => send('PUT', path, body),
    delete: (path) => send('DELETE', path),
    raw,
  };
}

export const apiClient: HttpClient = createHttpClient({
  baseUrl: API_BASE_URL,
  scopes: API_SCOPES,
  withSourceHeader: true,
  normalizeJson: true,
});

export const graphClient: HttpClient = createHttpClient({
  baseUrl: GRAPH_BASE_URL,
  scopes: GRAPH_SCOPES,
  withSourceHeader: false,
  normalizeJson: false,
});
